import React from 'react';
import styled from 'styled-components';
import PageBanner from '../components/common/PageBanner';
import ContentSection from '../components/common/ContentSection';
import SectionTitle from '../components/common/SectionTitle';
import Button from '../components/common/Button';
import { theme } from '../styles/GlobalStyles';

const IndustryGrid = styled.div`
  margin-top: 4rem;
`;

const IndustrySection = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 4rem;
  align-items: center;
  margin-bottom: 5rem;

  &:nth-child(even) { direction: rtl; > div { direction: ltr; } }

  @media (max-width: 992px) {
    grid-template-columns: 1fr !important;
    gap: 2rem;
    direction: ltr;
  }
`;

const IndustryImage = styled.div`
  background-size: cover;
  background-position: center;
  border-radius: 16px;
  height: 380px;
  border: 1px solid ${theme.border};
  box-shadow: 0 20px 60px rgba(0,0,0,0.4);

  @media (max-width: 992px) { height: 260px; }
`;

const IndustryContent = styled.div`
  h3 {
    font-size: 1.9rem;
    margin-bottom: 1.25rem;
    background: ${theme.gradient};
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
  }

  p {
    margin-bottom: 1.25rem;
    line-height: 1.8;
    font-size: 1rem;
    color: ${theme.textMuted};
  }
`;

const FeatureList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 1.25rem 0;
  display: flex;
  flex-direction: column;
  gap: 0.6rem;
`;

const FeatureItem = styled.li`
  color: ${theme.textMuted};
  font-size: 0.92rem;
  padding-left: 1.4rem;
  position: relative;

  &::before {
    content: "✓";
    background: ${theme.gradient};
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
    position: absolute;
    left: 0;
    font-weight: 700;
  }
`;

const ButtonContainer = styled.div`
  margin-top: 1.75rem;
`;

const industries = [
  {
    img: 'https://images.unsplash.com/photo-1584308666744-24d5c474f2ae?ixlib=rb-4.0.3&auto=format&fit=crop&w=1170&q=80',
    title: 'Pharmaceuticals',
    desc: 'Counterfeit drugs endanger lives. Prodseal ensures only genuine medicines reach patients, with solutions designed for the pharmaceutical industry\'s unique regulatory requirements.',
    features: ['Regulatory-compliant authentication solutions', 'Track medication from production to patient', 'Easy verification for healthcare professionals', 'Detection of diverted or expired products'],
  },
  {
    img: 'https://images.unsplash.com/photo-1580913428023-02c695666d61?ixlib=rb-4.0.3&auto=format&fit=crop&w=1074&q=80',
    title: 'FMCG',
    desc: 'From packaged food to cosmetics, counterfeiting destroys consumer trust. Prodseal builds brand protection into everyday purchases, ensuring consumers get exactly what they pay for.',
    features: ['Cost-effective authentication for high-volume products', 'Simple integration with existing packaging', 'Enhanced customer engagement opportunities', 'Supply chain visibility and protection'],
  },
  {
    img: 'https://images.unsplash.com/photo-1483985988355-763728e1935b?ixlib=rb-4.0.3&auto=format&fit=crop&w=1170&q=80',
    title: 'Luxury & Fashion',
    desc: 'Every luxury product carries identity and prestige. Prodseal keeps exclusivity intact by giving customers proof of originality, protecting both reputation and revenue for premium brands.',
    features: ['Elegant authentication solutions that enhance brand prestige', 'Secondary market verification to maintain value', 'Luxury consumer experience with digital product passports', 'Limited edition product authentication'],
  },
  {
    img: 'https://images.unsplash.com/photo-1519389950473-47ba0277781c?ixlib=rb-4.0.3&auto=format&fit=crop&w=1170&q=80',
    title: 'Electronics',
    desc: 'Warranty fraud is costly. With Prodseal, customers scan once to confirm authenticity and activate warranty instantly, protecting both consumers and manufacturers.',
    features: ['Digital warranty management integrated with authentication', 'Protection against grey market imports', 'Component-level authentication for complex products', 'Streamlined returns and repairs process'],
  },
];

const IndustriesPage = () => (
  <>
    <PageBanner
      title="Industries We Serve"
      subtitle="Prodseal's authentication technology helps protect products across diverse sectors."
    />

    <ContentSection>
      <SectionTitle>Protecting Products Across Every Industry</SectionTitle>
      <IndustryGrid>
        {industries.map(({ img, title, desc, features }) => (
          <IndustrySection key={title}>
            <IndustryImage style={{ backgroundImage: `url('${img}')` }} />
            <IndustryContent>
              <h3>{title}</h3>
              <p>{desc}</p>
              <FeatureList>
                {features.map((f) => <FeatureItem key={f}>{f}</FeatureItem>)}
              </FeatureList>
              <ButtonContainer>
                <Button primary to="/solutions">Learn More</Button>
              </ButtonContainer>
            </IndustryContent>
          </IndustrySection>
        ))}
      </IndustryGrid>
    </ContentSection>
  </>
);

export default IndustriesPage;
